import React, { useState, useEffect, useCallback } from 'react';
import { 
    Zap, Heart, Shield, Star, Repeat, 
    Target, Award, CheckCircle, XCircle 
} from 'lucide-react';
import './Describing.css';

// --- Configuración ---
const TOTAL_ROUNDS = 5;
const INITIAL_LIVES = 3;
const CORRECT_ADJECTIVES_PER_ROUND = 8;

function Describing() {
    // --- Estados del Juego (sin cambios) ---
    const [gameState, setGameState] = useState('menu');
    const [character, setCharacter] = useState(null);
    // Una imagen que no carga tiene que caer al emoji, no dejar el hueco.
    const [imagenRota, setImagenRota] = useState(false);
    const [adjectives, setAdjectives] = useState([]);
    const [selectedAdjectives, setSelectedAdjectives] = useState([]);
    const [feedbackHistory, setFeedbackHistory] = useState([]);
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(INITIAL_LIVES);
    const [round, setRound] = useState(1);
    const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('describingHighScore') || '0', 10));
    const [correctCount, setCorrectCount] = useState(0);
    const [totalCorrectAnswers, setTotalCorrectAnswers] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // --- Lógica del Juego (sin cambios funcionales) ---
    const fetchRoundData = useCallback(async (seenCharacters = []) => {
        setIsLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        try {
            // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
            const response = await fetch('/api/minigames/get-describing-round', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ 
                    difficulty: 1, 
                    seen_characters: seenCharacters 
                }),
            });
            if (!response.ok) throw new Error('No se encontraron personajes.');
            
            const data = await response.json();
            setCharacter(data);
            setImagenRota(false);

            const correct = [...data.correct_adjectives].sort(() => 0.5 - Math.random()).slice(0, CORRECT_ADJECTIVES_PER_ROUND);
            const incorrect = [...data.incorrect_adjectives].sort(() => 0.5 - Math.random()).slice(0, 2);
            setAdjectives([...correct, ...incorrect].sort(() => 0.5 - Math.random()));

            setGameState('playing');
        } catch (err) {
            setError(err.message);
            setGameState('menu');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const startGame = () => {
        setScore(0);
        setLives(INITIAL_LIVES);
        setRound(1);
        setSelectedAdjectives([]);
        setFeedbackHistory([]);
        setCorrectCount(0);
        setTotalCorrectAnswers(0);
        fetchRoundData();
    };
    
    const nextRound = useCallback(() => {
        setRound(r => r + 1);
        setSelectedAdjectives([]);
        setFeedbackHistory([]);
        setCorrectCount(0);
        fetchRoundData([character?.character_name]);
    }, [character, fetchRoundData]);

    const endGame = useCallback((finalScore = score, finalTotalCorrect = totalCorrectAnswers) => {
        if (finalScore > highScore) {
            setHighScore(finalScore);
            localStorage.setItem('describingHighScore', finalScore.toString());
        }

        const saveScore = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                
                if (finalTotalCorrect > 0) {
                    await fetch('/api/minigames/save-score', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            game_name: 'describing',
                            score: finalScore,
                            correct_answers: finalTotalCorrect,
                            total_questions: TOTAL_ROUNDS * CORRECT_ADJECTIVES_PER_ROUND
                        })
                    });
                }
            } catch (error) {
                console.error("Error guardando XP:", error);
            }
        };
        saveScore();

        setGameState('gameOver');
    }, [score, highScore, totalCorrectAnswers]);

    useEffect(() => {
        if (correctCount >= CORRECT_ADJECTIVES_PER_ROUND) {
            const timer = setTimeout(() => {
                if (round >= TOTAL_ROUNDS) {
                    endGame(score, totalCorrectAnswers);
                } else {
                    nextRound();
                }
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [correctCount, round, nextRound, endGame, score, totalCorrectAnswers]);

    useEffect(() => {
        if (lives <= 0) {
            const timer = setTimeout(() => endGame(score, totalCorrectAnswers), 1500);
            return () => clearTimeout(timer);
        }
    }, [lives, endGame, score, totalCorrectAnswers]);

    const handleAdjectiveSelect = (adjective) => {
        if (selectedAdjectives.some(adj => adj.english === adjective.english)) return;
        const isCorrect = character.correct_adjectives.some(correct => correct.english === adjective.english);
        const newSelection = { ...adjective, isCorrect };
        setSelectedAdjectives(prev => [...prev, newSelection]);
        setFeedbackHistory(prev => [newSelection, ...prev]);

        if (isCorrect) {
            setScore(s => s + 10);
            const newCorrectCount = correctCount + 1;
            setCorrectCount(newCorrectCount);
            setTotalCorrectAnswers(t => t + 1);
            
            if (newCorrectCount === CORRECT_ADJECTIVES_PER_ROUND) {
                setScore(s => s + 50);
            }
        } else {
            setLives(l => l - 1);
        }
    };
    
    const resetGame = () => {
        setGameState('menu');
        setError('');
    };

    // --- Renderizado con Clases Corregidas ---
    if (gameState === 'menu') {
        return (
            <div className="ds-container">
                <div className="ds-inner-container">
                    <div className="ds-menu">
                        <div className="ds-header">
                            <Shield size={40} />
                            <h1>Describing</h1>
                            <p>Selecciona los adjetivos que describen correctamente al personaje.</p>
                        </div>
                        <div className="ds-dashboard">
                            <div><Star size={16} /><span>Puntaje Máx.</span><strong>{highScore}</strong></div>
                            <div><Target size={16} /><span>Rondas</span><strong>{TOTAL_ROUNDS}</strong></div>
                            <div><Heart size={16} /><span>Vidas</span><strong>{INITIAL_LIVES}</strong></div>
                        </div>
                        <div className="ds-start-section">
                            <div className="ds-rules">
                                <p>+10 pts por acierto</p>
                                <p>+50 bonus por encontrar los {CORRECT_ADJECTIVES_PER_ROUND} adjetivos</p>
                            </div>
                            {error && <p className="ds-error-message">{error}</p>}
                            <button className="ds-play-btn" onClick={startGame} disabled={isLoading}>
                                {isLoading ? 'Cargando...' : '¡Comenzar a Describir!'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (gameState === 'gameOver') {
        return (
            <div className="ds-container">
                <div className="ds-inner-container">
                    <div className="ds-game-over">
                        <Award size={48} />
                        <h2>¡Juego Terminado!</h2>
                        <p>Tu puntaje final fue:</p>
                        <strong className="ds-final-score">{score}</strong>
                        <button className="ds-play-btn" onClick={resetGame}>
                            <Repeat size={16}/> Jugar de Nuevo
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="ds-container">
             <div className="ds-inner-container playing">
                {/* PREVENCIÓN: Renombramos game-hud para evitar conflictos */}
                <div className="ds-game-hud">
                    <div className="ds-hud-item score"><Star size={16}/> {score}</div>
                    <div className="ds-hud-item round"><Target size={16}/> Ronda {round} / {TOTAL_ROUNDS}</div>
                    <div className="ds-hud-item lives">
                        {Array.from({ length: INITIAL_LIVES }).map((_, i) => (
                            <Heart key={i} size={20} className={i < lives ? 'filled' : 'empty'}/>
                        ))}
                    </div>
                </div>

                <div className="ds-main-content">
                    <div className="ds-character-display">
                        {/* ── LA IMAGEN, CON RESPALDO ──────────────────────
                            Las cinco rutas de la base apuntaban a
                            `/images/characters/`, una carpeta que no existe:
                            los cinco personajes salían con la imagen partida.
                            Y este `<img>` no tenía `onError`, así que no había
                            nada detrás.

                            Ahora manda el emoji cuando no hay fichero — y
                            también cuando lo hay pero no carga, que es el caso
                            que nadie ve venir. La IA genera personajes con
                            emoji porque no puede inventar ficheros. */}
                        {character?.character_image && !imagenRota ? (
                            <img
                                src={character.character_image}
                                alt={character.character_name}
                                className="ds-character-image"
                                onError={() => setImagenRota(true)}
                            />
                        ) : (
                            <div className="ds-character-image ds-character-emoji">
                                {character?.character_emoji || "🙂"}
                            </div>
                        )}
                        <h2>Describe a: {character?.character_name}</h2>
                        <p>Encuentra {Math.max(0, CORRECT_ADJECTIVES_PER_ROUND - correctCount)} adjetivos correctos más</p>
                    </div>
                    
                    <div className="ds-adjectives-grid">
                        {adjectives.map((adj) => {
                            const isSelected = selectedAdjectives.some(sel => sel.english === adj.english);
                            return (
                                <button 
                                    key={adj.english}
                                    className={`ds-adjective-btn ${isSelected ? 'disabled' : ''}`}
                                    onClick={() => handleAdjectiveSelect(adj)}
                                    disabled={isSelected}
                                >
                                    {adj.english}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="ds-feedback-area">
                    {feedbackHistory.length === 0 && <div className="ds-feedback-placeholder">Tus respuestas aparecerán aquí...</div>}
                    {feedbackHistory.map((feedback, index) => (
                        <div key={`${feedback.english}-${index}`} className={`ds-feedback-card ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
                            {feedback.isCorrect ? <CheckCircle size={20}/> : <XCircle size={20} />}
                            <div className="ds-feedback-text">
                                <p>"{character.character_name}" is {feedback.isCorrect ? '' : 'NOT'} <strong>{feedback.english}</strong></p>
                                <small>{feedback.spanish}</small>
                            </div>
                        </div>
                    ))}
                </div>
             </div>
        </div>
    );
}

export default Describing;